import React, { useState, useEffect, useMemo } from "react";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { useTheme } from "@mui/material/styles";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";

import GlobalAlert from "components/Notification";
import InfoCardField from "components/cards/InfoCardField";
import IconTag from "components/StatusTag/IconTag";
import StatTrendLineChart from "pages/AInsight/Overview/StatTrendLineChart";

import icn_line_chart from 'assets/images/icons/icn_line_chart.svg';

const ignoreErrorList = [
  "AI Insight subscription required",
  "UnityPro subscription required",
]

const OverviewCardConfig = [
  { label: "Service Experience", key: "serviceExperience", tabTarget: "service_experience" },
  { label: "Security", key: "security", tabTarget: "security" },
  { label: "Roam", key: "roam", tabTarget: "roam" },
  { label: "Wireless Coverage", key: "wirelesCoverage", tabTarget: "wireless_coverage" },
  { label: "Link Integrity", key: "linkIntegrity", tabTarget: "link_integrity" },
  { label: "Operations", key: "operations", tabTarget: "operations" }
];

const levelConfig = {
  "Good": "green",
  "Warning": "orange",
  "Critical": "red"
}

const Overview = ({ timeRange, handleTab }) => {
  const theme = useTheme();

  const styleSheet = {
    "Good": theme.palette.primary.greenPrimary,
    "Warning": theme.palette.primary.orangeThird,
    "Critical": theme.palette.primary.redPrimary,
  };

  const { auth, standalone, isAuthReady } = useAuthScope();

  const [loading, setLoading] = useState(false);
  const [overviewUuid, _setOverviewUuid] = useState(uuidv4());
  const [overviewData, setOverviewData] = useState([]);
  const [overviewAverageLevelData, setOverviewAverageLevelData] = useState([]);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  function getTransformData(series) {
    const map = new Map();

    series.forEach(([date, metrics]) => {
      const timestamp = Math.floor(new Date(date).getTime() / 1000);

      Object.entries(metrics).forEach(([key, value]) => {
        if (!map.has(key)) {
          map.set(key, []);
        }

        map.get(key).push({
          timestamp,
          usage: value,
        });
      });
    });

    return Array.from(map.entries()).map(([key, data]) => ({
      key,
      data,
    }));
  }

  function getGrowthRate(values) {
    let diff = 0;

    if (values?.length >= 2) {
      const first = values[0];
      const last = values[values.length - 1];

      diff = Number((last - first).toFixed(1));
    }

    return `${diff > 0 ? '+' : ''}${diff}%`;
  };

  function getLastData(values) {
    let last = 0;

    if (values?.length >= 1) {
      last = values[values.length - 1];
    }

    return {
      value: last,
      level: last >= 90 ? "Good" : last >= 60 ? "Warning" : "Critical",
      label: `${last}%`
    };
  };

  function getAverageLevelData(series) {
    const temp = {};

    series.forEach(([_, metrics]) => {
      Object.entries(metrics).forEach(([key, value]) => {
        if (!temp[key]) {
          temp[key] = { total: 0, count: 0, values: [] };
        }

        if (value !== null) {
          temp[key].total += value;
          temp[key].count += 1;
          temp[key].values.push(value);
        }
      });
    });

    return Object.entries(temp).map(([key, { total, count, values }]) => {
      const avg = count ? Math.round(total / count) : 0;
      const growthRate = getGrowthRate(values);

      const { level, label } = getLastData(values);

      return {
        key,
        avg,
        level,
        growthRate,
        last: label
      };
    });
  }

  function onRedirectToTab(e, tabTarget) {
    handleTab(e, tabTarget);
  }

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  function fetchReadOverviewData() {
    setLoading(true)
    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    sendRequest(
      standalone
        ? "/orgs/:orgId/sites/:siteId/aiInsight/dashboard.read"
        : "/ents/:entId/orgs/:orgId/sites/:siteId/aiInsight/dashboard.read",
      overviewUuid,
      restfulParams,
    );
  }

  useEffect(() => {
    if (isAuthReady) {
      fetchReadOverviewData()
    }
  }, [isAuthReady])

  useEffect(() => {
    const readResponse = getData(overviewUuid);
    if (readResponse?.code && readResponse.code !== 200) {
      const isIgnoreError = ignoreErrorList.includes(readResponse.message);

      setLoading(false);
      if (isIgnoreError) return;

      setAlertInfo({
        open: true,
        severity: "error",
        message: readResponse.message,
      });
    }
    if (readResponse && readResponse.data) {
      const data = readResponse.data;

      const transformData = getTransformData(data.series);
      const averageData = getAverageLevelData(data.series);

      setOverviewData(transformData)
      setOverviewAverageLevelData(averageData)

      setLoading(false)
    }
  }, [dataMap])

  const overviewDataMap = useMemo(() => (
    Object.fromEntries(overviewData.map(i => [i.key, i.data]))
  ), [overviewData]);

  const overviewLevelMap = useMemo(() => (
    Object.fromEntries(overviewAverageLevelData.map(i => [i.key, i]))
  ), [overviewAverageLevelData]);

  return (
    <Box>
      {
        loading ?
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box> :
          <Grid container spacing={2}>
            {OverviewCardConfig.map((item) => {
              const chartData = overviewDataMap[item.key];
              const levelData = overviewLevelMap[item.key];

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={item.key}
                  sx={{ cursor: "pointer" }}
                  onClick={e => onRedirectToTab(e, item.tabTarget)}
                >
                  <InfoCardField
                    title={
                      <Box sx={{ display: "flex" }}>
                        <img src={icn_line_chart} alt="" />
                        <span style={{ marginLeft: 10 }}>{item.label}</span>
                      </Box>
                    }
                    metaInfo={levelData ? <IconTag label={levelData.level} /> : null}
                    infoData={
                      chartData ? (
                        <Box sx={{ position: "relative", width: "100%", height: "auto", minHeight: "230px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "end",
                              width: "100%",
                              pr: "15px"
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: theme.typography.customFontWeight[3],
                                fontSize: theme.typography.customFontSize[11],
                                color: styleSheet[levelData.level],
                                zIndex: 10
                              }}
                            >
                              {/* {levelData.avg}% */}
                              {levelData.last}
                            </Typography>

                            {/* <Typography
                              sx={{
                                fontWeight: theme.typography.customFontWeight[0],
                                fontSize: theme.typography.customFontSize[1],
                                color: theme.palette.primary.greyPrimary,
                                zIndex: 10,
                                whiteSpace: "nowrap"
                              }}
                            >
                              {timeRange}
                            </Typography> */}
                          </Box>
                          <Box sx={{ mt: "20px" }}>
                            <StatTrendLineChart
                              lineChartData={chartData}
                              lineColor={levelConfig[levelData.level]}
                            />
                          </Box>
                          <Box
                            sx={{
                              position: "absolute",
                              right: "15px",
                              bottom: "20px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: theme.typography.customFontWeight[3],
                                fontSize: theme.typography.customFontSize[4],
                                color: styleSheet[levelData.level],
                                zIndex: 10
                              }}
                            >
                              {levelData.growthRate}
                            </Typography>
                          </Box>
                        </Box>
                      ) : <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          minHeight: "234px"
                        }}>
                        <Typography
                          sx={{
                            color: theme.palette.primary.greyThird,
                            fontSize: theme.typography.customFontSize[2],
                            margin: 8
                          }}>
                          No data available
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              );
            })}
          </Grid>
      }
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default Overview;
